import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  isAnyOf,
} from "@reduxjs/toolkit";
import {
  InstoreTemplate,
  InstoreTemplateState,
  InstoreTemplateParams,
  InstoreTemplateRow,
} from "../../models/instoreTemplate";
import { FieldValues } from "react-hook-form";
import { RootState } from "../../store/configureStore";
import { toast } from "react-toastify";
import getAxiosParams from "../../helpers/getAxiosParams";
import agent from "../../api/agent";

//#region INITIAL STATE
function initParams(): InstoreTemplateParams {
  return {
    page: 1,
    limit: 25,
    sort: JSON.stringify({ column: "name", order: "desc" }),
  };
}

const initialExtraState: InstoreTemplateState = {
  instoreTemplateRowsLoaded: false,
  instoreTemplates_Loaded: false,
  instoreTemplate_params: initParams(),
  lastLoadedTemplateId: null,
  metaData: undefined,
  loading: false,
  changed: false,
  status: "iddle",
  activeWeekTemplates: [],
  activeWeekTemplatesLoaded: false,
};
//#endregion

//#region CREATE ADAPTERS
const instoreTemplatesAdapter = createEntityAdapter<InstoreTemplate>();
const instoreTemplateRowsAdapter = createEntityAdapter<InstoreTemplateRow>();
//#endregion

//#region READ
// Fetch All
export const fetchInstoreTemplatesAsync = createAsyncThunk<
  InstoreTemplate[],
  void,
  { state: RootState }
>("instoreTemplates/fetchInstoreTemplatesAsync", async (_, thunkAPI) => {
  const instoreTemplateParams = getAxiosParams(
    thunkAPI.getState().instoreTemplates.instoreTemplate_params,
  );
  try {
    const { data, meta } = await agent.InstoreTemplates.list(
      instoreTemplateParams,
    );
    thunkAPI.dispatch(setInstoreTemplateMetaData(meta));
    return data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});

// Fetch the active "Week" templates whose date window covers today, filtered
// server-side (order type + active + between_from_and_until) so the result is
// never cut off by the paginated list (default limit 25). `today` is passed as
// YYYY-MM-DD by the caller. Stored separately from the main list.
export const fetchActiveWeekInstoreTemplatesAsync = createAsyncThunk<
  InstoreTemplate[],
  string,
  { state: RootState }
>(
  "instoreTemplates/fetchActiveWeekInstoreTemplatesAsync",
  async (today, thunkAPI) => {
    const params = new URLSearchParams();
    params.append("order_type_instore_name", "Week");
    params.append("active", "1");
    params.append("between_from_and_until", today);
    // Generous limit: there is ~one Week template per instore type, so this is
    // a handful — but avoid re-introducing a pagination cut-off.
    params.append("limit", "1000");
    try {
      const { data } = await agent.InstoreTemplates.list(params);
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  },
);

// Fetch by id
export const fetchInstoreTemplateAsync = createAsyncThunk<
  InstoreTemplate,
  number,
  { state: RootState }
>("instoreTemplates/fetchInstoreTemplateAsync", async (id, thunkAPI) => {
  try {
    const { data } = await agent.InstoreTemplates.detail(id);
    return data;
  } catch (error: any) {
    const raw = error?.response?.data?.message ?? error?.message;
    const message =
      typeof raw === "string"
        ? raw
        : error?.response?.status === 403
          ? "You don't have permission to view this template."
          : "Something went wrong while loading this template.";
    return thunkAPI.rejectWithValue(message);
  }
});
//#endregion

//#region CREATE
export const createInstoreTemplateAsync = createAsyncThunk<
  InstoreTemplate,
  { values: FieldValues | null }
>(
  "instoreTemplates/createInstoreTemplateAsync",
  async ({ values }, thunkAPI) => {
    try {
      const { data } = await agent.InstoreTemplates.create({ ...values });
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  },
);
//#endregion

//#region UPDATE
export const updateInstoreTemplateAsync = createAsyncThunk<
  InstoreTemplate,
  { id: number; values: FieldValues }
>(
  "instoreTemplates/updateInstoreTemplateAsync",
  async ({ id, values }, thunkAPI) => {
    try {
      const { data } = await agent.InstoreTemplates.update(id, { ...values });
      return data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message);
    }
  },
);
//#endregion

//#region DELETE
export const deleteInstoreTemplateAsync = createAsyncThunk<
  number,
  { id: number }
>("instoreTemplates/deleteInstoreTemplateAsync", async ({ id }, thunkAPI) => {
  try {
    await agent.InstoreTemplates.delete(id);
    return id;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(error.response.data.message);
  }
});
//#endregion

const initialState = {
  templates: instoreTemplatesAdapter.getInitialState(),
  templateRows: instoreTemplateRowsAdapter.getInitialState(),
  ...initialExtraState,
};

//#region CREATE REDUCER SLICE
export const instoreTemplateSlice = createSlice({
  name: "instoreTemplates",
  initialState,
  reducers: {
    setInstoreTemplateParams: (state, action) => {
      state.instoreTemplates_Loaded = false;
      state.instoreTemplate_params = {
        ...state.instoreTemplate_params,
        ...action.payload,
        page: 1,
      };
    },
    upsertInstoreTemplateRowLocal: (state, action) => {
      instoreTemplateRowsAdapter.upsertOne(state.templateRows, action.payload);
      state.changed = true;
    },
    upsertManyInstoreTemplateRowLocal: (state, action) => {
      instoreTemplateRowsAdapter.upsertMany(state.templateRows, action.payload);
      state.changed = true;
    },
    removeInstoreTemplateRowLocal: (state, action) => {
      instoreTemplateRowsAdapter.removeOne(state.templateRows, action.payload);
      state.changed = true;
    },
    removeManyInstoreTemplateRowLocal: (state, action) => {
      instoreTemplateRowsAdapter.removeMany(state.templateRows, action.payload);
      state.changed = true;
    },
    clearInstoreTemplateParams: (state) => {
      state.instoreTemplates_Loaded = false;
      state.instoreTemplate_params = initParams();
    },
    clearInstoreTemplateParam: (state, action) => {
      const newParams = { ...state.instoreTemplate_params };
      delete newParams[action.payload];
      state.instoreTemplates_Loaded = false;
      state.instoreTemplate_params = newParams;
    },
    setInstoreTemplatePageNumber: (state, action) => {
      state.instoreTemplates_Loaded = false;
      state.instoreTemplate_params = {
        ...state.instoreTemplate_params,
        ...action.payload,
      };
    },
    setInstoreTemplateMetaData: (state, action) => {
      state.metaData = action.payload;
    },
    resetInstoreTemplateParams: (state) => {
      state.instoreTemplates_Loaded = false;
      state.instoreTemplate_params = initParams();
    },
    clearInstoreTemplates: () => initialState,
  },
  extraReducers: (builder) => {
    // FETCH instoreTemplates
    builder.addCase(fetchInstoreTemplatesAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreTemplates";
    });
    builder.addCase(
      fetchInstoreTemplatesAsync.fulfilled,
      (state, { payload }) => {
        instoreTemplatesAdapter.setAll(state.templates, payload);
        state.instoreTemplates_Loaded = true;
        state.lastLoadedTemplateId = null;
        state.loading = false;
        state.status = "idle";
      },
    );

    // FETCH instoreTemplate by id and set rows
    builder.addCase(fetchInstoreTemplateAsync.pending, (state) => {
      state.loading = true;
      state.status = "pendingFetchInstoreTemplate";
    });
    builder.addCase(
      fetchInstoreTemplateAsync.fulfilled,
      (state, { payload }) => {
        instoreTemplatesAdapter.upsertOne(state.templates, payload);
        instoreTemplateRowsAdapter.setAll(
          state.templateRows,
          payload.template_rows,
        );
        state.instoreTemplateRowsLoaded = true;
        state.lastLoadedTemplateId = payload.id;
        state.loading = false;
        state.changed = false;
        state.status = "idle";
      },
    );

    // CREATE instoreTemplate
    builder.addCase(createInstoreTemplateAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingCreateInstoreTemplate";
    });
    builder.addCase(
      createInstoreTemplateAsync.fulfilled,
      (state, { payload }) => {
        toast.success("Instore order template added");
        state.metaData &&= {
          ...state.metaData,
          total: state.metaData.total + 1,
          to: state.metaData.to + 1,
        };
        state.instoreTemplateRowsLoaded = false;
        state.instoreTemplates_Loaded = false;
        state.lastLoadedTemplateId = payload.id;
        state.loading = false;
        state.changed = false;
        state.status = "idle";
      },
    );

    // UPDATE instoreTemplate
    builder.addCase(updateInstoreTemplateAsync.pending, (state, { meta }) => {
      state.loading = true;
      state.status = "PendingUpdateInstoreTemplate" + meta.arg.id;
    });
    builder.addCase(
      updateInstoreTemplateAsync.fulfilled,
      (state, { payload }) => {
        instoreTemplatesAdapter.upsertOne(state.templates, payload);
        instoreTemplateRowsAdapter.setAll(
          state.templateRows,
          payload.template_rows,
        );
        toast.success(`Template ${payload.name} updated`);
        state.instoreTemplates_Loaded = false;
        state.loading = false;
        state.changed = false;
        state.status = "idle";
      },
    );

    // DELETE instoreTemplate
    builder.addCase(deleteInstoreTemplateAsync.pending, (state) => {
      state.loading = true;
      state.status = "PendingDeleteInstoreTemplate";
    });
    builder.addCase(
      deleteInstoreTemplateAsync.fulfilled,
      (state, { payload }) => {
        instoreTemplatesAdapter.removeOne(state.templates, payload);
        state.templateRows = instoreTemplateRowsAdapter.getInitialState();
        toast.success("Template deleted");
        state.lastLoadedTemplateId = null;
        state.metaData &&= {
          ...state.metaData,
          total:
            state.metaData.total > 0
              ? state.metaData.total - 1
              : state.metaData.total,
          to: state.metaData.to > 0 ? state.metaData.to - 1 : state.metaData.to,
        };
        state.loading = false;
        state.changed = false;
        state.status = "idle";
      },
    );

    // FETCH active Week templates (kept separate from the main list; does not
    // touch shared loading/status, and a failure is silent — the caller simply
    // sees no active template).
    builder.addCase(
      fetchActiveWeekInstoreTemplatesAsync.fulfilled,
      (state, { payload }) => {
        state.activeWeekTemplates = payload;
        state.activeWeekTemplatesLoaded = true;
      },
    );
    builder.addCase(fetchActiveWeekInstoreTemplatesAsync.rejected, (state) => {
      state.activeWeekTemplatesLoaded = true;
    });

    // CRUD REJECTION
    builder.addMatcher(
      isAnyOf(
        fetchInstoreTemplateAsync.rejected,
        fetchInstoreTemplatesAsync.rejected,
        createInstoreTemplateAsync.rejected,
        updateInstoreTemplateAsync.rejected,
        deleteInstoreTemplateAsync.rejected,
      ),
      (state, { payload }) => {
        state.templateRows = instoreTemplateRowsAdapter.getInitialState();
        toast.error(`${payload}`);
        state.lastLoadedTemplateId = null;
        state.loading = false;
        state.status = "idle";
      },
    );
    //
  },
});
//#endregion

//#region SELECTORS
export const instoreTemplatesSelectors = instoreTemplatesAdapter.getSelectors(
  (state: RootState) => state.instoreTemplates.templates,
);

export const instoreTemplateRowSelectors =
  instoreTemplateRowsAdapter.getSelectors(
    (state: RootState) => state.instoreTemplates.templateRows,
  );
//#endregion

//#region EXPORT ACTIONS
export const {
  removeManyInstoreTemplateRowLocal,
  upsertManyInstoreTemplateRowLocal,
  removeInstoreTemplateRowLocal,
  upsertInstoreTemplateRowLocal,
  setInstoreTemplatePageNumber,
  setInstoreTemplateMetaData,
  resetInstoreTemplateParams,
  clearInstoreTemplateParams,
  clearInstoreTemplateParam,
  setInstoreTemplateParams,
  clearInstoreTemplates,
} = instoreTemplateSlice.actions;
//#endregion

export default instoreTemplateSlice.reducer;
